import "./lib/browser-compat";

import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";

import "@/styles/globals.css";
import { ClerkProvider } from "@clerk/clerk-react";
import { QueryClientProvider } from "@tanstack/react-query";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
// Must stay first: it calls initAnalytics(), so the client below is already
// configured (or deliberately inert outside production) by the time React mounts.
import "./instrumentation.ts";

import { ApiAuthSync } from "./components/auth/api-auth-sync";
import { CONSTANTS } from "./config/constants.tsx";
import { relayOAuthResultToOpener } from "./features/channel/helpers/oauth-popup";
import { initializeMockData } from "./mock";
import { router } from "./router.tsx";

import { queryClient } from "@/lib/query-client";

export { queryClient };

declare global {
  // `var` is what types a `globalThis` property; the root survives HMR on it.
  // eslint-disable-next-line no-var
  var __app_root: ReactDOM.Root | undefined;
}

// An OAuth popup only exists to carry a code back to the window that opened
// it, so it never mounts the app.
const isClosingOAuthPopup = relayOAuthResultToOpener();

// Initialize mock data if enabled
if (!isClosingOAuthPopup) {
  initializeMockData();
}

if (!isClosingOAuthPopup) {
  const rootElement = document.getElementById("root");

  if (!rootElement) {
    throw new Error("Root element not found");
  }

  // Reuse root during HMR
  const root = globalThis.__app_root ?? ReactDOM.createRoot(rootElement);

  if (!globalThis.__app_root) {
    globalThis.__app_root = root;
  }

  root.render(
    <ClerkProvider
      afterSignOutUrl="/auth/sign-in"
      publishableKey={CONSTANTS.CLERK_PUBLISHABLE_KEY}
    >
      <ApiAuthSync />
      <QueryClientProvider client={queryClient}>
        {/* Nothing reads this yet - `track` uses the singleton directly. It's here
            so feature flags and surveys are a hook away rather than a refactor. */}
        <PostHogProvider client={posthog}>
          <RouterProvider router={router} />
        </PostHogProvider>
      </QueryClientProvider>
    </ClerkProvider>,
  );
}
